import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Progress, Select, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CalendarOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  MachineryActivityGroup,
  MachinerySummaryRow,
  headTagColor,
  machineryRowKey,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  num,
} from "./machineryShared";

// Machinery register SEGREGATED BY ACTIVITY (machinery rates are
// activity-scoped — each face/adit carries its own rate card): bold activity
// group rows with aggregated qty / cost-weighted unit price / cost / share,
// their norm items nested beneath in cost order. TOOLS rows (no activity)
// collect into one "Tools & Machinery" pseudo-group (activityId 0).
//
// SERVER-DRIVEN: the table fetches its own page of activity groups from
// machinery-activity-summary — pagination (10 activities/page), activity-name
// search (400ms debounce) and the Total Cost sort all run server-side.
// Groups arrive collapsed and expandedRowKeys resets on every fetch, so each
// page/search/sort/filter change starts collapsed by default.
// % shares are of the grand total, which includes the non-itemized
// consumables bucket, so shares don't sum to 100.

interface ItemRow extends MachinerySummaryRow {
  key: string;
  qtyNum: number;
  rateNum: number;
  costNum: number;
  isGroup?: false;
  /**
   * EQUIPMENT rows only: the nine engine cost heads, so a user can see what
   * the Unit Price above is MADE OF — ₹ per unit of output, head by head.
   * They reuse the item columns exactly (name / UOM / rate / cost), because a
   * head row IS an item row at a finer grain.
   */
  children?: HeadRow[];
}

/** One cost head under an EQUIPMENT row. Σ rateNum reconciles to the parent. */
interface HeadRow {
  key: string;
  isGroup?: false;
  isHead: true;
  itemName: string;
  itemCode: string;
  uomName: string;
  qtyNum: number;
  rateNum: number;
  costNum: number;
}

/** Title case for the engine's head keys ("depreciation" -> "Depreciation"). */
const HEAD_LABELS: Record<string, string> = {
  depreciation: "Depreciation",
  interest: "Interest",
  insurance: "Insurance",
  repair: "Repair",
  misc: "Misc",
  electricity: "Electricity",
  fuel: "Fuel",
  lubricants: "Lubricants",
  crew: "Crew",
};

interface GroupRow {
  key: string;
  isGroup: true;
  activityName: string;
  itemCount: number;
  costNum: number;
  qtySum: number;
  // Cost-weighted average across the activity's items: Σcost / Σqty.
  avgRate: number;
  // Single UOM when every item under the activity agrees, else "" (mixed).
  uomName: string;
  taskSum: number;
  lineSum: number;
  children: ItemRow[];
}

type TableRow = ItemRow | GroupRow | HeadRow;

const isHeadRow = (row: TableRow): row is HeadRow =>
  (row as HeadRow).isHead === true;

type SortDir = "asc" | "desc";

const PAGE_SIZE = 10;
const GROUP_ROW_BG = "#f5f7fa";
const INK = "#022c57";

const MachinerySummaryTable: React.FC<{
  projectId: number | string;
  phaseId?: number;
  activityId?: number;
  /** Machinery head API value (e.g. 'MUCKING' / 'TOOLS'); undefined = All. */
  head?: string;
  /** Bump to refetch (page Refresh button). */
  refreshTick?: number;
  grandTotal: number;
  // Drill-down: clicking an item's task count opens the phase → activity →
  // task breakdown drawer for that machinery item.
  onTasksClick?: (row: MachinerySummaryRow) => void;
  // Clicking the item NAME opens its month-by-month schedule instead.
  onItemClick?: (row: MachinerySummaryRow) => void;
}> = ({
  projectId,
  phaseId,
  activityId,
  head,
  refreshTick,
  grandTotal,
  onTasksClick,
  onItemClick,
}) => {
  const wbsName = useWbsName();
  const [search, setSearch] = useState("");
  // What the server was actually asked for — trails `search` by 400ms.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);
  const [apiGroups, setApiGroups] = useState<MachineryActivityGroup[]>([]);
  const [activityCount, setActivityCount] = useState(0);
  const [loading, setLoading] = useState(false);
  // Controlled so every fetch can reset it → groups collapsed by default.
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  // Debounce the search input before it hits the server; committing a new
  // term also rewinds to page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Page filters changed above the table — start over from page 1. (Declared
  // before the fetch effect; a stale-page fetch in the same commit is
  // cancelled by the cleanup below.)
  useEffect(() => {
    setPage(1);
  }, [phaseId, activityId, head]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    // Collapse everything whenever the visible group set is about to change.
    setExpandedRowKeys([]);
    threeMReportApis
      .get3MMachineryActivitySummary(projectId, {
        phaseId,
        activityId,
        head,
        search: debouncedSearch || undefined,
        sortDir,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) {
          setApiGroups(response?.data?.groups || []);
          setActivityCount(num(response?.data?.activityCount));
        } else {
          setApiGroups([]);
          setActivityCount(0);
        }
      })
      .catch((err) => {
        console.error("Error loading machinery activity register:", err);
        if (!cancelled) {
          setApiGroups([]);
          setActivityCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    phaseId,
    activityId,
    head,
    debouncedSearch,
    sortDir,
    page,
    refreshTick,
  ]);

  // Server groups → antd tree rows. Item children keep the MachinerySummaryRow
  // shape (the drill-down drawer consumes the row as-is).
  const groups = useMemo<GroupRow[]>(
    () =>
      apiGroups.map((group) => {
        const groupActivityId = group.activityId ?? 0;
        const children: ItemRow[] = (group.items || []).map((m) => {
          const rowKey = machineryRowKey(m);
          // Only EQUIPMENT rows carry a head split; Mucking norms and tools
          // have no cost-head grain, so they stay leaves.
          const heads = ((m as any).equipmentHeads || []) as Array<{
            head: string;
            amount: string;
            perUnit?: string;
          }>;
          return {
            ...m,
            key: rowKey,
            qtyNum: num(m.totalQuantity),
            rateNum: num(m.avgRate),
            costNum: num(m.totalCost),
            children: heads.length
              ? heads.map((h) => ({
                  key: `${rowKey}-HEAD-${h.head}`,
                  isHead: true as const,
                  itemName: HEAD_LABELS[h.head] || h.head,
                  itemCode: "",
                  // perUnit is absent when this norm's matrix mixes units —
                  // blank the UOM too so the row never implies a unit it
                  // cannot name.
                  uomName: h.perUnit !== undefined ? m.uomName || "" : "",
                  qtyNum: 0,
                  rateNum: h.perUnit !== undefined ? num(h.perUnit) : 0,
                  costNum: num(h.amount),
                }))
              : undefined,
          };
        });
        return {
          key: `GROUP-ACT-${groupActivityId}`,
          isGroup: true as const,
          activityName:
            group.activityName ||
            (groupActivityId === 0
              ? "Tools & Machinery (no activity)"
              : "Unlinked activity"),
          itemCount: num(group.itemCount),
          costNum: num(group.totalCost),
          qtySum: num(group.totalQuantity),
          avgRate: num(group.avgRate),
          uomName: group.uomName || "",
          taskSum: num(group.taskCount),
          lineSum: num(group.lineCount),
          children,
        };
      }),
    [apiGroups]
  );

  const columns: ColumnsType<TableRow> = [
    {
      title: "Activity / Machinery Item",
      key: "itemName",
      render: (_: any, row) => {
        if (row.isGroup) {
          return (
            <span style={{ fontWeight: 600, color: INK }} title={row.activityName}>
              {wbsName(row.activityName)}
              <span
                style={{ fontWeight: 400, color: "#8c8c8c", marginLeft: 8 }}
              >
                ({row.itemCount} item{row.itemCount === 1 ? "" : "s"})
              </span>
            </span>
          );
        }
        if (isHeadRow(row)) {
          // A cost head of the parent EQUIPMENT row — quieter than an item,
          // and never clickable (there is no drilldown at this grain).
          return (
            <span style={{ color: "#5d6f83", fontSize: 12.5 }}>
              · {row.itemName}
            </span>
          );
        }
        const item = row as ItemRow;
        return (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              {item.head ? (
                <Tag
                  color={headTagColor(item.head)}
                  style={{ marginRight: 0, fontSize: 11 }}
                >
                  {item.head}
                </Tag>
              ) : null}
              {onItemClick ? (
                <Tooltip title="Monthly schedule — quantity & cost by month">
                  <Button
                    type="link"
                    size="small"
                    onClick={() => onItemClick(item)}
                    style={{
                      padding: 0,
                      height: "auto",
                      fontWeight: 500,
                      textAlign: "left",
                      whiteSpace: "normal",
                    }}
                  >
                    {item.itemName || "—"}
                    <CalendarOutlined
                      style={{ fontSize: 12, marginLeft: 6, opacity: 0.65 }}
                    />
                  </Button>
                </Tooltip>
              ) : (
                <span style={{ fontWeight: 500 }}>{item.itemName || "—"}</span>
              )}
            </div>
            {item.itemCode ? (
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                {item.itemCode}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "UOM",
      key: "uomName",
      width: 90,
      render: (_: any, row) =>
        row.isGroup ? (
          <span style={{ fontWeight: 600, color: INK }}>
            {row.uomName || (row.itemCount > 1 ? "Mixed" : "—")}
          </span>
        ) : (
          (row as ItemRow).uomName || "—"
        ),
    },
    {
      title: "Total Qty",
      key: "qty",
      width: 130,
      align: "right",
      render: (_: any, row) =>
        row.isGroup ? (
          <Tooltip
            title={
              row.uomName
                ? "Sum of item quantities under this activity"
                : "Sum across items with differing UOMs — read with care"
            }
          >
            <span
              style={{
                fontVariantNumeric: "tabular-nums",
                fontWeight: 600,
                color: INK,
              }}
            >
              {fmtQty(row.qtySum)}
            </span>
          </Tooltip>
        ) : isHeadRow(row) ? (
          // A head has no quantity of its own — it shares the parent's.
          <span style={{ color: "#c8cfd8" }}>—</span>
        ) : (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {fmtQty((row as ItemRow).qtyNum)}
          </span>
        ),
    },
    {
      title: "Unit Price",
      key: "rate",
      width: 140,
      align: "right",
      render: (_: any, row) =>
        row.isGroup ? (
          <Tooltip title="Cost-weighted average: activity cost ÷ activity quantity">
            <span
              style={{
                fontVariantNumeric: "tabular-nums",
                fontWeight: 600,
                color: INK,
              }}
            >
              {fmtINR(row.avgRate)}
            </span>
          </Tooltip>
        ) : (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {fmtINR((row as ItemRow).rateNum)}
          </span>
        ),
    },
    {
      title: "Total Cost",
      key: "cost",
      width: 130,
      align: "right",
      render: (_: any, row) => (
        <Tooltip title={fmtINR(row.costNum)}>
          <span
            style={{
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              cursor: "default",
              color: row.isGroup ? INK : undefined,
            }}
          >
            {fmtINRCompact(row.costNum)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "% Share",
      key: "share",
      width: 150,
      render: (_: any, row) => {
        const share = grandTotal > 0 ? (row.costNum / grandTotal) * 100 : 0;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Progress
              percent={Math.min(100, Math.round(share * 10) / 10)}
              size="small"
              strokeWidth={4}
              showInfo={false}
              strokeColor={row.isGroup ? INK : "#2f54eb"}
              style={{ flex: 1, minWidth: 60, margin: 0 }}
            />
            <span
              style={{
                fontSize: 12,
                color: "#595959",
                fontVariantNumeric: "tabular-nums",
                width: 42,
                textAlign: "right",
              }}
            >
              {share.toFixed(1)}%
            </span>
          </div>
        );
      },
    },
    {
      title: "Tasks",
      key: "tasks",
      width: 80,
      align: "right",
      render: (_: any, row) => {
        if (row.isGroup) {
          return (
            <Tooltip title="Sum of item task counts (a task may appear under several items)">
              <span
                style={{ fontVariantNumeric: "tabular-nums", color: "#595959" }}
              >
                {row.taskSum}
              </span>
            </Tooltip>
          );
        }
        const item = row as ItemRow;
        const value = num(item.taskCount);
        if (!onTasksClick || !(value > 0)) return value;
        return (
          <Tooltip title="View phase → activity → task breakdown">
            <Button
              type="link"
              size="small"
              onClick={() => onTasksClick(item)}
              style={{
                padding: 0,
                height: "auto",
                fontVariantNumeric: "tabular-nums",
                fontWeight: 600,
              }}
            >
              {value}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: "Lines",
      key: "lines",
      width: 80,
      align: "right",
      render: (_: any, row) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {row.isGroup ? row.lineSum : num((row as ItemRow).lineCount)}
        </span>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <h6 style={{ fontWeight: 600, marginBottom: 2, color: INK }}>
          Activity-wise Machinery Register
        </h6>
        <span style={{ color: "#8c8c8c", fontSize: 13 }}>
          Machinery norms grouped by activity — expand an activity for its
          item-wise rates
        </span>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "nowrap",
          gap: 10,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Input
          allowClear
          size="large"
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          placeholder="Search activity name"
          // Grows to fill the row (min 320px); the sort select and count keep
          // fixed widths so everything stays on one line, heights matched via
          // size="large" on both controls.
          style={{ flex: "1 1 320px", minWidth: 320 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select<SortDir>
          size="large"
          value={sortDir}
          onChange={(value) => {
            setSortDir(value);
            setPage(1);
          }}
          style={{ flex: "0 0 232px", width: 232 }}
          options={[
            {
              value: "desc",
              label: (
                <span>
                  <SortDescendingOutlined style={{ marginRight: 6 }} />
                  Total Cost: High → Low
                </span>
              ),
            },
            {
              value: "asc",
              label: (
                <span>
                  <SortAscendingOutlined style={{ marginRight: 6 }} />
                  Total Cost: Low → High
                </span>
              ),
            },
          ]}
        />
        <span
          style={{
            color: "#8c8c8c",
            fontSize: 13,
            marginLeft: "auto",
            whiteSpace: "nowrap",
          }}
        >
          {activityCount} activit{activityCount === 1 ? "y" : "ies"}
        </span>
      </div>
      <Table<TableRow>
        size="small"
        columns={columns}
        dataSource={groups}
        loading={loading}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
        }}
        onRow={(row) =>
          row.isGroup ? { style: { background: GROUP_ROW_BG } } : {}
        }
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: activityCount,
          showSizeChanger: false,
          showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} activities`,
        }}
        onChange={(pagination) => setPage(pagination.current || 1)}
        scroll={{ x: 1050 }}
      />
    </>
  );
};

export default MachinerySummaryTable;
